// src/pages/Events.jsx
import React, { useEffect, useState } from "react";
import { getEventsForMasjid, PLACEHOLDER_IMAGE } from "../services/eventService";

export default function EventsPage({ setCurrentPage, events: initialEvents = [], masjidId }) {
  const [activeFilter, setActiveFilter] = useState("ongoing"); // ongoing | future | past
  const [events, setEvents] = useState(initialEvents);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // SAMPLE fallback
  const SAMPLE_EVENTS = React.useMemo(() => {
    const now = new Date();
    const pad = (n) => String(n).padStart(2, "0");
    const fmt = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
    const pastStart = new Date(now); pastStart.setDate(now.getDate() - 7); pastStart.setHours(13, 0, 0, 0);
    const pastEnd = new Date(pastStart); pastEnd.setHours(14, 0, 0, 0);
    const ongoingStart = new Date(now); ongoingStart.setHours(now.getHours() - 1, 0, 0, 0);
    const ongoingEnd = new Date(now); ongoingEnd.setHours(now.getHours() + 1, 0, 0, 0);
    const futureStart = new Date(now); futureStart.setDate(now.getDate() + 7); futureStart.setHours(14, 0, 0, 0);
    const futureEnd = new Date(futureStart); futureEnd.setHours(16, 0, 0, 0);
    return [
      { id: "past-1", name: "Youth Halaqa (Past)", description: "Completed session last week.", startDate: fmt(pastStart), endDate: fmt(pastEnd), startTime: "01:00 PM", endTime: "02:00 PM", location: "BICC Hall", image: PLACEHOLDER_IMAGE },
      { id: "ongoing-1", name: "Quran Study (Ongoing)", description: "Session currently in progress.", startDate: fmt(ongoingStart), endDate: fmt(ongoingEnd), startTime: "01:00 PM", endTime: "03:00 PM", location: "Main Prayer Area", image: PLACEHOLDER_IMAGE },
      { id: "future-1", name: "Community Potluck (Future)", description: "Bring a dish and meet your neighbors!", startDate: fmt(futureStart), endDate: fmt(futureEnd), startTime: "02:00 PM", endTime: "04:00 PM", location: "BICC Courtyard", image: PLACEHOLDER_IMAGE },
    ];
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setError("");
        const data = masjidId ? await getEventsForMasjid(masjidId) : [];
        if (cancelled) return;
        let list = Array.isArray(data) ? data : [];
        if (!list.length) list = SAMPLE_EVENTS;
        setEvents(list);
      } catch (e) {
        setError("Failed to load events.");
        setEvents(SAMPLE_EVENTS);
      } finally {
        setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [masjidId, SAMPLE_EVENTS]);

  const getFirst = (obj, keys) => { for (const k of keys) if (obj?.[k] != null && obj[k] !== "") return obj[k]; return null; };
  const safeParseDate = (v) => { if (!v) return null; const d = new Date(v); return isNaN(d) ? null : d; };
  const isSameDay = (a, b) => a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
  const combineDateTime = (dateVal, timeVal) => {
    const date = safeParseDate(dateVal); if (!date) return null;
    if (!timeVal || typeof timeVal !== "string") return date;
    const parts = timeVal.trim().split(/\s+/); const time = parts[0] || ""; const mer = (parts[1] || "").toUpperCase();
    let [h, m] = time.split(":").map((n) => parseInt(n || 0, 10));
    if (mer === "PM" && h < 12) h += 12; if (mer === "AM" && h === 12) h = 0;
    date.setHours(h || 0, m || 0, 0, 0); return date;
  };
  const categorizeEvent = (event) => {
    const now = new Date();
    const startDate = getFirst(event, ["startDate", "start_date", "date", "start"]);
    const endDate = getFirst(event, ["endDate", "end_date"]);
    const startTime = getFirst(event, ["startTime", "start_time", "time"]);
    const endTime = getFirst(event, ["endTime", "end_time"]);
    const start = combineDateTime(startDate, startTime);
    const end = combineDateTime(endDate || startDate, endTime);
    if (start && end) { if (start <= now && now <= end) return "ongoing"; if (now < start) return "future"; return "past"; }
    if (start) { if (isSameDay(start, now)) return "ongoing"; if (now < start) return "future"; return "past"; }
    return "future";
  };

  const counts = (events || []).reduce((acc, e) => { const b = categorizeEvent(e); acc[b] = (acc[b] || 0) + 1; return acc; }, { ongoing: 0, future: 0, past: 0 });
  const allEmpty = counts.ongoing === 0 && counts.future === 0 && counts.past === 0;
  const filteredEvents = allEmpty ? (events || []) : (events || []).filter((e) => categorizeEvent(e) === activeFilter);

  const TabButton = ({ id, label, count }) => (
    <button
      onClick={() => setActiveFilter(id)}
      className={`px-6 py-2 rounded-lg font-semibold transition-colors border ${activeFilter === id ? "bg-[#D2B995] text-white border-[#D2B995]" : "bg-white text-gray-700 border-gray-200 hover:bg-[#F5F1E8]"
        }`}
    >
      {label} <span className="ml-1 text-xs text-gray-500">({count})</span>
    </button>
  );

  return (
    <section className="events pt-6 pb-10 bg-gray-50">
      <div className="w-full px-4">
        <div className="text-center mb-6">
          <h2 className="text-3xl font-bold text-gray-800 mb-4">BICC Events</h2>
          <div className="flex items-start justify-start gap-3">
            <TabButton id="ongoing" label="Ongoing" count={counts.ongoing} />
            <TabButton id="future" label="Future" count={counts.future} />
            <TabButton id="past" label="Past" count={counts.past} />
          </div>
        </div>

        {loading ? (
          <div className="text-center py-4"><p className="text-gray-500">Loading events…</p></div>
        ) : error ? (
          <div className="text-center py-4"><p className="text-red-600">{error}</p></div>
        ) : filteredEvents.length === 0 ? (
          <div className="text-center py-4">
            <p className="text-gray-500">No {allEmpty ? "events" : activeFilter + " events"} right now.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredEvents.map((event, index) => {
              const imgSrc = event?.image || PLACEHOLDER_IMAGE;
              const title = event?.name || event?.title || "Event";
              return (
                <div key={event.id || `${title}-${index}`} className="bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300">
                  <div className="h-48 overflow-hidden">
                    <a data-fancybox="events-page" href={imgSrc} className="h-full w-full block">
                      <img
                        src={imgSrc}
                        alt={title}
                        className="h-full w-full object-cover hover:scale-105 transition-transform duration-300"
                        onError={(e) => {
                          if (e.currentTarget.src !== PLACEHOLDER_IMAGE) {
                            e.currentTarget.src = PLACEHOLDER_IMAGE;
                            e.currentTarget.onerror = null;
                          }
                        }}
                      />
                    </a>
                  </div>
                  <div className="p-6">
                    <h3 className="title text-xl font-bold text-gray-800 mb-3">{title}</h3>
                    <ul className="space-y-2 mb-3">
                      {(event.schedule || event.startDate || event.date || event.start) && (
                        <li className="flex items-center text-gray-600">
                          <span className="text-green-700 mr-2">📅</span>
                          {event.schedule ||
                            (event.startDate || event.date || event.start
                              ? new Date(event.startDate || event.date || event.start).toLocaleDateString()
                              : "")}
                        </li>
                      )}
                      {(event.time || event.startTime || event.start_time) && (
                        <li className="flex items-center text-gray-600">
                          <span className="text-green-700 mr-2">⏰</span>
                          {event.time || event.startTime || event.start_time}
                        </li>
                      )}
                      {event.location && (
                        <li className="flex items-center text-gray-600">
                          <span className="text-green-700 mr-2">📍</span>
                          {event.location}
                        </li>
                      )}
                    </ul>
                    {event.description && <p className="text-gray-600 leading-relaxed">{event.description}</p>}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
}
